/**
 * Date-only utilities for fields like formation_date, deadline_date, due_date.
 *
 * These fields are calendar dates (legal facts), NOT points in time.
 * `new Date("2025-08-19")` parses as UTC midnight, which shifts to Aug 18
 * in negative-UTC timezones. These utilities extract the YYYY-MM-DD components
 * directly and use UTC methods to prevent any timezone shift.
 */

// The date may be followed only by an ISO (`T`) or SQLite (space) time.
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})(?=$|[T ])/

/**
 * Safely parse a date-only string (YYYY-MM-DD) into a Date at noon UTC.
 * Extracts year/month/day via regex to avoid any timezone interpretation.
 * Noon UTC ensures both local and UTC methods return the correct calendar
 * date in every timezone on Earth (UTC-12 to UTC+14).
 * Also handles ISO timestamps by stripping the time portion.
 */
export function parseDateOnly(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null
  const match = dateStr.match(DATE_ONLY_RE)
  if (!match) return null
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const d = new Date(Date.UTC(year, month - 1, day, 12))
  // Date.UTC rolls 2025-02-30 into March and maps years 0-99 to 19xx.
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null
  return d
}

/**
 * Normalize a date-like string to YYYY-MM-DD.
 * Accepts YYYY-MM-DD and ISO datetime strings.
 */
export function normalizeDateOnly(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null
  const match = dateStr.match(DATE_ONLY_RE)
  if (!match) return null
  return `${match[1]}-${match[2]}-${match[3]}`
}

/**
 * Convert a Date object to a date-only string (YYYY-MM-DD) using local calendar
 * components. This is intended for user-selected dates in the UI.
 */
export function toDateOnlyString(date: Date | null | undefined): string {
  if (!date || isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format a date-only string safely. Uses UTC components to guarantee
 * the displayed date matches the stored YYYY-MM-DD value regardless of timezone.
 *
 * Supported tokens: yyyy, MM, M, dd, d, MMMM, MMM
 */
export function formatDateOnly(dateStr: string | null | undefined, formatStr: string = 'MM/dd/yyyy'): string {
  const d = parseDateOnly(dateStr)
  if (!d) return '-'

  const year = d.getUTCFullYear()
  const month = d.getUTCMonth() // 0-indexed
  const day = d.getUTCDate()

  const MONTHS_LONG = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  return formatStr.replace(/yyyy|MMMM|MMM|MM|M|dd|d/g, (token) => {
    switch (token) {
      case 'yyyy':
        return String(year)
      case 'MMMM':
        return MONTHS_LONG[month]
      case 'MMM':
        return MONTHS_SHORT[month]
      case 'MM':
        return String(month + 1).padStart(2, '0')
      case 'M':
        return String(month + 1)
      case 'dd':
        return String(day).padStart(2, '0')
      case 'd':
        return String(day)
      default:
        return token
    }
  })
}

/** `Oct 17, 2026` from an ISO date or timestamp; a dash when there is none. */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '-'
  return formatDateOnly(iso.slice(0, 10), 'MMM d, yyyy')
}
